import { Inject, Injectable } from '@angular/core';

import { Store } from '@ngrx/store';
import { withLatestFrom } from 'rxjs/operators';

import { CurrentUserHttpClient, CurrentUserSelectors } from '@recruitee/account/data';
import { COMPANY_ID, filterBoolean } from '@recruitee/common';
import { FeatureFlag, FeatureFlagSelectors } from '@recruitee/feature-flags/data';
import { AuthHelpers, AuthService } from '@recruitee/http';
import { Company } from '@recruitee/http/types';
import { IntercomService } from '@recruitee/intercom-integration';
import { setDefaultTimezone } from '@recruitee/time';
import { WindowRef } from '@recruitee/user-interface';

import { AccountActions, AccountSelectors } from '../store/account.state';

@Injectable({ providedIn: 'root' })
export class Account {
  constructor(
    @Inject(COMPANY_ID) private companyId: number,
    private windowRef: WindowRef,
    private authService: AuthService,
    private authHelpers: AuthHelpers,
    private currentUserService: CurrentUserHttpClient,
    private intercomService: IntercomService,
    private store: Store,
  ) {
    this.store
      .select(AccountSelectors.currentCompany)
      .pipe(
        filterBoolean,
        withLatestFrom(
          this.store.select(CurrentUserSelectors.data),
          this.store.select(FeatureFlagSelectors.featureEnabled(FeatureFlag.EventDebug)),
        ),
      )
      .subscribe(([currentCompany, currentAdmin, debugEventTracking]) =>
        this.intercomService.init(currentCompany, currentAdmin!, debugEventTracking),
      );
  }

  public redirectToBlockedAccount(): void {
    this.authService.redirectToBlockedAccount();
  }

  public setDefaultTimezone(timezone: string): void {
    setDefaultTimezone(timezone);
  }

  public changeCompany(currentCompany: Company, company: { id: number }): void {
    if (currentCompany!.ssoActive) {
      return this.authService.redirectToSignIn(company.id);
    }

    const nativeWindow = this.windowRef.nativeWindow;

    this.authHelpers.clearCompanyCookies();
    this.authHelpers.saveCompanyInCookie(company.id.toString());
    nativeWindow.location.href = nativeWindow.document.baseURI + '#/dashboard/overview';
    nativeWindow.location.reload();
  }

  public deleteAccountRequest(secureChangeToken: string): void {
    this.currentUserService
      .deleteAccountRequest(secureChangeToken)
      .pipe(filterBoolean)
      .subscribe(() =>
        this.store.dispatch(
          AccountActions.signOut({ payload: { afterSignOutUrl: this.authService.getAuthUrl() } }),
        ),
      );
  }

  public signOut(afterSignOutUrl?: string): void {
    this.intercomService.shutdown();
    this.authService.clearAccessToken();
    this.authService.redirectToSignOut(afterSignOutUrl);
  }

  public reloadApp(): void {
    this.windowRef.nativeWindow.location.reload();
  }
}
